import { forwardRef, type Ref, type ReactNode, type SelectHTMLAttributes } from 'react';
import { InputWrapper, type InputWrapperBaseProps } from '../shared/InputBase.js';
import './Select.css';

export interface SelectItem {
  label: ReactNode;
  value: string;
  disabled?: boolean;
}

export interface SelectProps
  extends InputWrapperBaseProps,
    Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'color'> {
  data: Array<string | SelectItem>;
  placeholder?: string;
  clearable?: boolean;
}

/**
 * Native `<select>` themed to match the design system. Stage 3 will ship a
 * fully a11y-comboboxed `<Select>` backed by `<Popover>` + roving tab-index.
 */
export const Select = forwardRef(function Select(
  props: SelectProps,
  ref: Ref<HTMLSelectElement>
): ReactNode {
  const {
    label, description, error, required, withAsterisk,
    leftSection, rightSection, leftSectionWidth, rightSectionWidth,
    size, className, style, id,
    data, placeholder, clearable,
    ...selectProps
  } = props;

  const items = data.map((d) => (typeof d === 'string' ? { label: d, value: d } : d));

  const chevron = rightSection ?? (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1,1 5,5 9,1" />
    </svg>
  );

  return (
    <InputWrapper
      label={label}
      description={description}
      error={error}
      required={required}
      withAsterisk={withAsterisk}
      leftSection={leftSection}
      rightSection={chevron}
      leftSectionWidth={leftSectionWidth}
      rightSectionWidth={rightSectionWidth ?? 28}
      size={size}
      className={className}
      style={style}
      inputId={id}
    >
      {({ inputId, describedBy, invalid, className: ctlClass }) => (
        <select
          ref={ref}
          id={inputId}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={`${ctlClass} rr-select__native`}
          {...selectProps}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {items.map((it) => (
            <option key={it.value} value={it.value} disabled={it.disabled}>{it.label}</option>
          ))}
        </select>
      )}
    </InputWrapper>
  );
});
